
import { useState, useEffect } from 'react';
import Icon from '../../../components/ui/AppIcon';

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [expandedFAQ, setExpandedFAQ] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      id: '1',
      question: 'Is the Online MBA from D.Y. Patil UGC approved?',
      answer: 'Yes, our Online MBA program is fully approved by the University Grants Commission (UGC) and recognized by the Government of India. The degree holds the same value as a regular on-campus MBA and is accepted by all government and private sector organizations.',
      category: 'Approval',
    },
    {
      id: '2',
      question: 'What is the eligibility criteria for admission?',
      answer: 'You need a bachelor\'s degree in any discipline from a recognized university with a minimum of 50% marks (45% for reserved categories). Work experience is preferred but not mandatory. We welcome both fresh graduates and working professionals.',
      category: 'Eligibility',
    },
    {
      id: '3',
      question: 'Can I study while working full-time?',
      answer: 'Absolutely! Our program is specifically designed for working professionals. All lectures are recorded and available 24/7. Live sessions are scheduled on weekends. You can study at your own pace and complete assignments according to your schedule.',
      category: 'Flexibility',
    },
    {
      id: '4',
      question: 'What is the total duration of the program?',
      answer: 'The Online MBA program is 2 years (4 semesters). However, we offer flexible completion options. You can complete it faster if you have more time, or take up to 4 years if you need a slower pace due to work commitments.',
      category: 'Duration',
    },
    {
      id: '5',
      question: 'Are there any hidden charges apart from the fees?',
      answer: 'No, the ₹1,89,000 fee is all-inclusive. It covers tuition, study materials, examination fees, and lifetime access to recorded lectures. There are no admission fees, processing charges, or any other hidden costs.',
      category: 'Fees',
    },
    {
      id: '6',
      question: 'Will I get the same degree as regular MBA students?',
      answer: 'Yes, you will receive the exact same degree as on-campus MBA students. The degree certificate does not mention "online" or "distance" anywhere. It is a regular MBA degree from D.Y. Patil University with full UGC recognition.',
      category: 'Validity',
    },
    {
      id: '7',
      question: 'How are exams conducted for online students?',
      answer: 'Exams are conducted online through our secure proctored platform. You can take exams from home at your convenience within the scheduled exam window. We also have physical exam centers in major cities if you prefer offline exams.',
      category: 'Exams',
    },

      {
      id: '8',
      question: 'Does an Online MBA Hold the Same Value as a Regular MBA?',
      answer: 'The UGC has formally confirmed that Distance or Online MBA and Regular MBA degrees are equally valid and recognized.',
      category: 'Exams',
    },
       {
      id: '9',
      question: 'Are EMI payment options available?',
      answer: 'We have loan partners that allow you to pay the program fee in easy EMIs for up to 24 months',
      category: 'Exams',
    },
         {
      id: '10',
      question: 'What Is the Admission Process?',
      answer: 'Click the “Apply Now” button on this page and submit your details. A dedicated career counsellor will contact you to complete the registration. After document verification, you can proceed with the admission fee payment.',
      category: 'Exams',
    },
    // {
    //   id: '8',
    //   question: 'Is EMI facility available for fee payment?',
    //   answer: 'Yes, we offer interest-free EMI options from 6 to 24 months. You can pay as low as ₹10,417 per month. The EMI approval process is quick (24-48 hours) and requires minimal documentation.',
    //   category: 'Fees',
    // },
    // {
    //   id: '9',
    //   question: 'What kind of placement support do you provide?',
    //   answer: 'While we don\'t guarantee placements (as most students are already employed), we provide career counseling, resume building workshops, interview preparation, and access to our corporate network for job opportunities and internal promotions.',
    //   category: 'Career',
    // },
    // {
    //   id: '10',
    //   question: 'Can I change my specialization after starting?',
    //   answer: 'Yes, you can change your specialization until the end of the first year. Specialization courses start from the third semester, giving you time to explore different areas and make an informed decision.',
    //   category: 'Specialization',
    // },
  ];

  const filteredFAQs = isHydrated && searchQuery
    ? faqs.filter(
        (faq) =>
          faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : faqs;

  const handleFAQToggle = (faqId: string) => {
    if (!isHydrated) return;
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  return (
    <section className="py-10 bg-background">
      <div className="max-w-content mx-auto px-section-margin">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-text-secondary">
            Get answers to common questions about our Online MBA program
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8">
          <div className="relative">
            <Icon
              name="MagnifyingGlassIcon"
              size={20}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary"
              variant="outline"
            />
            <input
              type="text"
              placeholder="Search your question..."
              value={searchQuery}
              onChange={(e) => isHydrated && setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-muted border border-border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* FAQ List */}
        <div className="space-y-4">
          {filteredFAQs.map((faq) => {
            const isExpanded = isHydrated && expandedFAQ === faq.id;

            return (
              <button
                key={faq.id}
                onClick={() => handleFAQToggle(faq.id)}
                className="w-full text-left bg-card border border-border rounded-sm p-2 hover:shadow-form transition-default"
              >
                <div className="flex items-start justify-between space-x-4">
                  <div className="flex-1">
                    {/* <div className="flex items-center space-x-2 mb-2">
                      <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                        {faq.category}
                      </span>
                    </div> */}
                    <h3 className="text-lg font-bold text-text-primary faq_question_text">{faq.question}</h3>
                    {isExpanded && (
                      <p className="text-text-secondary leading-relaxed mt-4">{faq.answer}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-default ${
                        isExpanded ? 'bg-primary text-primary-foreground' : 'bg-muted text-text-secondary'
                      }`}
                    >
                      <Icon
                        name={isExpanded ? 'MinusIcon' : 'PlusIcon'}
                        size={20}
                        variant="solid"
                      />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {isHydrated && filteredFAQs.length === 0 && (
          <div className="text-center py-12">
            <Icon name="QuestionMarkCircleIcon" size={64} className="text-text-secondary mx-auto mb-4" variant="outline" />
            <p className="text-text-secondary">No FAQs found matching your search.</p>
          </div>
        )}

        {/* Still Have Questions */}
        <div className="mt-12 bg-primary/5 border border-primary/20 rounded-xl p-8 text-center">
          <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-primary mx-auto mb-4" variant="solid" />
          <h3 className="text-2xl font-bold text-text-primary mb-3">Still Have Questions?</h3>
          <p className="text-text-secondary mb-6">
            Our expert counselors are available to answer all your queries and help you make an informed decision
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+919405709042"
              className="inline-flex items-center justify-center space-x-2 bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-default"
            >
              <Icon name="PhoneIcon" size={20} variant="solid" />
              <span>Call: +91 9405709042</span>
            </a>
            <a
              href="https://wa.me/919730088168"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 bg-success text-success-foreground px-6 py-3 rounded-lg font-semibold hover:bg-success/90 transition-default"
            >
              <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
              <span>WhatsApp Chat</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;